import { Dispatch, SetStateAction, ChangeEvent } from "react";
import { SelectChangeEvent } from "@mui/material";
import { IFileObject, IImageInfo, IProjectData } from "@types";
import { ReportItem } from "../@types";
import { toast } from "react-toastify";
import { getAIInfo } from "../services/serverService";
import { getProjects } from "common-lib";
import { checkProjectName } from "../services/projectService";

export const toQuery = <T extends object>(f: T): Partial<T> =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v != null)) as Partial<T>;

export const SF = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif`;

// 웹페이지 접속 주소로 요청을 보내기 위한 함수
export function getIpAddress() {
  const ipAddress = `${window.location.origin}/api`;
  return ipAddress;
}

export function handleTextFieldChange(setValue: Dispatch<SetStateAction<string>>) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };
}

export function handleSelectChange(setValue: Dispatch<React.SetStateAction<string>>) {
  return (event: SelectChangeEvent<string>) => {
    setValue(event.target.value);
  };
}
export function handleSliderChange(setValue: React.Dispatch<React.SetStateAction<number>>) {
  return (event: Event, newValue: number | number[]) => {
    if (typeof newValue === "number") {
      setValue(newValue);
    }
  };
}

export function handleMonacoChange(setValue: Dispatch<SetStateAction<string>>) {
  return (value: string) => {
    setValue(value);
  };
}

//시간 포맷
export function formatDate(input: string | null | undefined): string {
  if (!input) {
    return "-";
  }
  try {
    const [datePart, timePart] = input.split("T");
    const [hours, minutes, seconds] = timePart.split(".")[0].split(":");
    return `${datePart} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return input;
  }
}

// 시간 차 계산
export function getTimeDiff(createAt: string, finishAt: string | null): string {
  if (finishAt === null) {
    return "-";
  }
  const startTime = new Date(createAt);
  const endTime = new Date(finishAt);

  const timeDifference = endTime.getTime() - startTime.getTime();

  // 밀리초를 시, 분, 초로 변환
  const seconds = Math.floor((timeDifference / 1000) % 60);
  const minutes = Math.floor((timeDifference / (1000 * 60)) % 60);
  const hours = Math.floor(timeDifference / (1000 * 60 * 60));

  // 결과 문자열 생성
  let result = "";
  if (hours > 0) {
    result += `${hours}시간 `;
  }
  if (minutes > 0 || hours > 0) {
    result += `${minutes}분 `;
  }
  result += `${seconds}초`;

  return result.trim();
}

// 소수점 첫째자리까지
export function formatNumber(value: number, point: number): string {
  if (isNaN(value)) {
    throw new Error("Invalid input: not a number");
  }

  return value.toFixed(point);
}

// 이미지 데이터 평탄화
export function imageFlat(images: any) {
  // return Object.keys(images)
  //   .map((imageType) => images[imageType].flat())
  //   .flat();
  return images as IImageInfo[];
}

export function handleSaveFilter(
  type: string,
  value: string,
  setSelectedReportList: Dispatch<SetStateAction<ReportItem[]>>,
  contentId: string,
) {
  setSelectedReportList((prev) =>
    prev.map((report) => {
      if (report.id === contentId) {
        const existingFilters = report.filter || [];
        const updatedFilters = existingFilters.filter((filter) => filter.type !== type);
        return {
          ...report,
          filter: [
            ...updatedFilters,
            {
              type: type,
              value: value,
            },
          ],
        };
      }
      return report;
    }),
  );
}

// 리포트 카드에 저장된 필터 값 조회
export function getSavedFilter(
  selectedReportList: ReportItem[],
  contentId: string,
  filterType: string,
  defaultValue: string,
): string {
  const savedReport = selectedReportList.find((item) => item.id === contentId);
  return savedReport?.filter?.find((filter) => filter.type === filterType)?.value ?? defaultValue;
}

// 시간 형식 변환
export function formatDateTime(dateString: string) {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function formatByTypeDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // 0-indexed
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// number초 -> n시간 n분 n초로 변환
export function formatHourByTime(second: number): string {
  const hours = Math.floor(second / 3600);
  const minutes = Math.floor((second % 3600) / 60);
  const seconds = second % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}시간`);
  if (minutes > 0) parts.push(`${minutes}분`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}초`);

  return parts.join(" ");
}

// 쿼리에 따른 api 요청 파라미터 정제 함수
export const buildQueryParams = (params: Record<string, any>) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, value.toString());
    }
  });

  return queryParams.toString();
};

// 검사 label 조회 SSE에서의 시간 포맷
export function formatDateInSSE(date: string): string {
  return encodeURIComponent(date).toString();
}

// 프로젝트가 학습 중이거나 대기열에 있는지 확인하는 함수
export async function checkProjectAIStatus(projectId: number): Promise<boolean> {
  try {
    const aiInfo = await getAIInfo();

    let isTrain = false;
    let isQueue = false;

    for (const item of aiInfo.data) {
      if (item.train.project_id === projectId) {
        isTrain = true;
        break;
      }
    }

    for (const item of aiInfo.data) {
      for (const queueItem of item.queue) {
        if (queueItem.project_id === projectId) {
          isQueue = true;
          break;
        }
      }
    }

    if (isTrain) {
      toast.error("해당 프로젝트는 학습중입니다.");
      return true;
    }

    if (isQueue) {
      toast.error("해당 프로젝트는 대기열에 있습니다.");
      return true;
    }

    return false;
  } catch (error) {
    toast.error("AI 상태 확인 중 오류가 발생했습니다.");
    console.error(error);
    return true;
  }
}

export function formatNumber2(number: number) {
  if (typeof number !== "number") {
    throw new Error("Input must number");
  }
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

interface FileItem {
  id: number;
  path: string;
  label: string;
}

export function countServerFiles(data: FileItem[]) {
  return data.reduce<Record<string, number>>((acc, item) => {
    acc[item.label] = (acc[item.label] || 0) + 1;
    return acc;
  }, {});
}

// label: ["files"] 형태로 변환
export function transFiles(data: IFileObject[]) {
  const result: { [key: string]: string[] } = {};

  data.forEach((item) => {
    if (!result[item.label]) {
      result[item.label] = [];
    }
    result[item.label].push(item.path);
  });

  return result;
}

interface inspResultItem {
  path: string;
  label: string;
  score: number;
}

export function formatInspResult(response: inspResultItem[], threshold: number) {
  try {
    if (!Array.isArray(response)) {
      console.error("formatInspResult: response is not array", response);
      return [];
    }

    const formatted = response.map((item, idx) => ({
      id: idx,
      AILabel: item.score > threshold ? "NG" : "OK",
      label: item.label,
      score: item.score,
      path: item.path,
    }));

    const labeledImages = formatted.filter((img) => img.label);
    const unlabeledImages = formatted.filter((img) => !img.label);

    const sortedMatchedImages = labeledImages
      .filter((img) => img.label === img.AILabel)
      .sort((a, b) => a.score - b.score);

    const sortedUnmatchedImages = labeledImages
      .filter((img) => img.label !== img.AILabel)
      .sort((a, b) => a.score - b.score);

    const sortedUnlabeledImages = unlabeledImages.sort((a, b) => a.score - b.score);

    return [...sortedMatchedImages, ...sortedUnmatchedImages, ...sortedUnlabeledImages];
  } catch (error) {
    console.error("formatInspResult error:", error, response);
    return [];
  }
}

interface RecalculateResult {
  updatedList: IImageInfo[];
  overkillCount: number;
}

export const calculateByThreshold = (list: IImageInfo[], threshold: number): RecalculateResult => {
  let overkillCount = 0;
  const updatedList = list.map((item) => {
    const newAILabel = item.score < threshold ? "OK" : "NG";

    // overkill 조건: 기존 OK → 새 NG
    if (item.label === "OK" && newAILabel === "NG") {
      overkillCount++;
    }

    return {
      ...item,
      AILabel: newAILabel,
    };
  });

  return { updatedList, overkillCount };
};

export const parseStringToArray = (value: string): string[] => {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
};

export const formatRemainingTime = (seconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(seconds));

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  const parts: string[] = [];

  if (hours > 0) parts.push(`${hours}시간`);
  if (minutes > 0) parts.push(`${minutes}분`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}초`);

  return parts.join(" ");
};

export const getUniqueProjectName = async (baseName: string) => {
  let newName = baseName;
  let count = 1;

  // 중복 존재하면 계속 번호 증가
  while (await checkProjectName(newName)) {
    newName = `${baseName}(${count})`;
    count++;
  }

  return newName;
};

/**
 * review 컬럼 값을 표시용 라벨 목록으로 바꾼다.
 * - {"label_seg":[...], "label_cls":[...]} → 각 세그먼트의 properties.label (중복 제거)
 * - {"label_cls":["NG"]} → label_cls 값
 * - 기존 평문 값("OK"/"NG") → 그대로
 */
export const getReviewLabels = (raw?: string | null): string[] => {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    if (Array.isArray(v?.label_seg) && v.label_seg.length > 0) {
      return Array.from(new Set<string>(v.label_seg.map((s: any) => s?.properties?.label).filter(Boolean)));
    }
    if (Array.isArray(v?.label_cls)) return v.label_cls;
  } catch {}
  return [raw];
};

export const centeredRect = (width: number, height: number) => ({
  x: Math.max(0, Math.round((window.innerWidth - width) / 2)),
  y: Math.max(0, Math.round((window.innerHeight - height) / 2)),
  width,
  height,
});
